import {
  AfterContentInit,
  ChangeDetectorRef,
  Component,
  ContentChild,
  ContentChildren,
  DoCheck,
  ElementRef,
  EmbeddedViewRef,
  Injector,
  Input,
  IterableChangeRecord,
  IterableChanges,
  IterableDiffer,
  IterableDifferFactory,
  IterableDiffers,
  QueryList,
  ViewChild,
  ViewContainerRef,
  ViewRef,
  ChangeDetectionStrategy
} from '@angular/core';
import * as _ from 'lodash-es';
import {Subject} from 'rxjs';
import {map, startWith, takeUntil, tap} from 'rxjs/operators';
import {destroy$} from '../../destroyable';
import {DynamicComponentFactoryService} from '../../dynamic-component';
import {Table, TableColumn, TableColumnConfig} from './table';
import {TableBodyEmptyRowDirective} from './table-body-empty-row.directive';
import {TableBodyRowContext, TableBodyRowDirective} from './table-body-row.directive';
import {TableColumnTemplateContext} from './table-column-template-context';
import {TableColumnDirective} from './table-column.directive';
import {TableHeaderRowDirective} from './table-header-row.directive';
import {
    CommonTablePluginMessageType,
    TablePluginMsgColumnsConfig,
    TablePluginMsgColumnsConfigPreview
} from './table-plugins/common-table-plugin-messages';
import {TableColumnReorderPlugin} from './table-plugins/table-column-reorder';
import {TableColumnsResizerPlugin} from './table-plugins/table-columns-resizer';
import {TablePluginMessage} from './table-plugins/table-plugin';
import {TablePluginsDelegate} from './table-plugins/table-plugins-delegate';

interface ChangeActions<T> {
    insert?: (record: ChangeRecord<T>) => void;
    remove?: (record: ChangeRecord<T>) => void;
    move?: (record: ChangeRecord<T>) => void;
    identityChange?: (record: ChangeRecord<T>) => void;
    finalize?: () => void;
}

interface ChangeRecord<T> {
    item: T;
    previousIndex: number;
    adjustedPreviousIndex: number;
    currentIndex: number;
    adjustedCurrentIndex: number;
}

interface VisibleColumnMeta {
    columnConfig: TableColumnConfig;
    index: number;
    viewRefInsertionIndex: number; // Conversion from the directives order to the rendering order.
}

/**
 * Allows to pass row height via css variable, assigned to the host element of the Table component.
 * This height will be used for calculation how many empty rows should be appended to the table.
 * If this variable is not defined of falsy, then the row height will be detected by a dummy invisible row,
 * temporary appended to the table.
 */
const CSS_VAR_TABLE_EMPTY_ROW_HEIGHT = '--tableEmptyRowHeight';

@Component({
    selector: '[table]',
    templateUrl: './table.component.html',
    styleUrls: ['./table.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class TableComponent implements DoCheck, AfterContentInit {

    private static columnConfigTrackerByKey(index: number, item: TableColumnConfig): string {
        return item.key;
    }

    @Input()
    public table: Table;

    public readonly destroy$ = destroy$();

    public columnDirectives: TableColumnDirective[];

    public headerColumnDirectives: TableColumnDirective[];
    public columnDefIndexByKey: {[key: string]: number};
    public columnDefs: TableColumn[];
    public visibleColumnMetaByKey: {[key: string]: VisibleColumnMeta};

    @ContentChild(TableHeaderRowDirective, {static: true})
    public tableHeaderRowDirective: TableHeaderRowDirective;

    @ContentChild(TableBodyRowDirective, {static: true})
    public tableBodyRowDirective: TableBodyRowDirective;

    @ContentChild(TableBodyEmptyRowDirective, {static: true})
    public tableBodyEmptyRowDirective: TableBodyEmptyRowDirective;

    @ContentChildren(TableColumnDirective, {descendants: true})
    public columnDirectivesQueryList: QueryList<TableColumnDirective>;

    @ViewChild('afterTable', {read: ViewContainerRef, static: true})
    public afterTableViewContainerRef: ViewContainerRef;

    private arrayDifferFactory: IterableDifferFactory = this.differs.find([]);
    private columnConfigsDiffer: IterableDiffer<TableColumnConfig>; // To be created after content init.
    private columnDirectivesDiffer: IterableDiffer<TableColumnDirective> = this.arrayDifferFactory.create();
    private rowsDiffer: IterableDiffer<any>; // To be created after content init.

    private columnConfigChangeActions: ChangeActions<TableColumnConfig> = this.getColumnConfigChangeActions();
    private columnDirectiveChangeActions: ChangeActions<TableColumnDirective> = this.getColumnDirectiveChangeActions();
    private rowChangeActions: ChangeActions<any> = this.getRowChangeActions();

    private implementedColumnsConfig: TableColumnConfig[];
    private columnsConfigPreview: TableColumnConfig[] = null; // isNothingChanged compares to null

    private readonly tablePluginsDelegate = new TablePluginsDelegate(
        this.injector,
        [
            TableColumnsResizerPlugin,
            TableColumnReorderPlugin
        ]
    );

    /**
     * Tells, that {@link ngAfterContentInit} has been already called.
     */
    private isNgAfterContentInitFired: boolean = false;

    constructor(
        private elementRef: ElementRef,
        private differs: IterableDiffers,
        private viewContainerRef: ViewContainerRef,
        private changeDetectorRef: ChangeDetectorRef,
        private dynamicComponentFactoryService: DynamicComponentFactoryService,
        private injector: Injector
    ) {}

    public ngDoCheck(): void {
        this.updateEmptyRows();

        this.updateColumnsConfig();

        /**
         * When rows are rendered before {@link ngAfterContentInit} ({@link ngDoCheck} may be called earlier),
         * they may be empty, since {@link columnDirectivesQueryList}
         * may not detect appearing of new column directives in these new rows.
         * Next times rows will be updated in {@link ngDoCheck}.
         */
        if (this.isNgAfterContentInitFired) {
            this.updateRows(this.table.rows);
        }
    }

    public ngAfterContentInit(): void {
        this.isNgAfterContentInitFired = true;
        this.headerColumnDirectives = this.tableHeaderRowDirective.columnDirectivesQueryList.toArray();

        this.updateColumnDefs();
        this.updateImplementedColumnsConfig(this.table.columnsConfig);
        this.updateVisibleColumnsMeta(this.implementedColumnsConfig);

        this.initTablePlugins();

        this.columnDirectivesQueryList.changes.pipe(
            map(() => this.columnDirectivesQueryList.toArray()),
            startWith(this.headerColumnDirectives),
            tap(columnDirectives => this.updateColumnDirectives(columnDirectives)),
            takeUntil(this.destroy$)
        ).subscribe();

        this.columnConfigsDiffer = this.arrayDifferFactory.create(TableComponent.columnConfigTrackerByKey);
        this.columnConfigsDiffer.diff(this.implementedColumnsConfig); // Pass the initial value to the differ.
        this.updateColumnsConfig();

        const rowsTrackFn = this.table.rowTrackFn;
        this.rowsDiffer = this.arrayDifferFactory.create(rowsTrackFn);

        this.tablePluginsDelegate.tableReady();

        if (!this.table.disableEmptyRows) {
            this.tableBodyEmptyRowDirective = this.tableBodyEmptyRowDirective || this.tableBodyRowDirective;
        }

        this.initAvailableRowsCounter();
        this.initEmptyRows();

        /**
         * When rows are rendered before {@link ngAfterContentInit} ({@link ngDoCheck} may be called earlier),
         * they may be empty, since {@link columnDirectivesQueryList}
         * may not detect appearing of new column directives in these new rows.
         * Next times rows will be updated in {@link ngDoCheck}.
         */
        this.updateRows(this.table.rows);
    }

    private updateColumnDefs(): void {
        this.columnDefs = this.headerColumnDirectives.map(directive => directive.tableColumn as TableColumn);
        this.columnDefIndexByKey = this.columnDefs.reduce(
            (dict, columnDef, columnIndex) => {
                dict[columnDef.key] = columnIndex;

                return dict;
            },
            {} as {[key: string]: number}
        );
    }

    private updateImplementedColumnsConfig(columnConfigs: TableColumnConfig[]): void {
        const columnDirectivesDict = _.keyBy(
            this.headerColumnDirectives,
                columnDirective => (columnDirective.tableColumn as TableColumn).key
        );

        this.implementedColumnsConfig = columnConfigs
            .filter(columnConfig => columnDirectivesDict[columnConfig.key]);
    }

    private updateVisibleColumnsMeta(columnConfigs: TableColumnConfig[]): void {
        this.visibleColumnMetaByKey = columnConfigs.reduce(
            (dict, columnConfig, index) => {
                dict[columnConfig.key] = {
                    columnConfig,
                    index,
                    viewRefInsertionIndex: null // To be calculated below.
                } as VisibleColumnMeta;

                return dict;
            },
            {} as {[key: string]: VisibleColumnMeta}
        );

        this.headerColumnDirectives.reduce(
            (indexesPool, columnDirective) => {
                const visibleColumnMeta = this.visibleColumnMetaByKey[(columnDirective.tableColumn as TableColumn).key];

                if (visibleColumnMeta) {
                    const viewRefInsertionIndex = _.sortedIndex(indexesPool, visibleColumnMeta.index);

                    visibleColumnMeta.viewRefInsertionIndex = viewRefInsertionIndex;
                    indexesPool.splice(viewRefInsertionIndex, 0, visibleColumnMeta.index);
                }

                return indexesPool;
            },
            []
        );
    }

    private updateColumnsConfig(): void {
        if (this.columnConfigsDiffer) {
            const columnsConfig = this.getColumnsConfig();

            this.updateImplementedColumnsConfig(columnsConfig);

            const changes = this.columnConfigsDiffer.diff(this.implementedColumnsConfig);

            if (changes) {
                this.updateVisibleColumnsMeta(this.implementedColumnsConfig);
                this.tablePluginsDelegate.columnsSetup({
                    columnDefs: this.columnDefs,
                    columnsConfig: this.implementedColumnsConfig,
                    columnDefIndexByKey: this.columnDefIndexByKey,
                    columnsSizingSteps: this.table.columnsSizingSteps
                });
                this.applyChanges(changes, this.columnConfigChangeActions);
                this.tablePluginsDelegate.finalizeCellsRendering();
                this.tablePluginsDelegate.finalizeColumnsConfigChanges();
            }
        }
    }

    private updateRows(rows: any[]): void {
        const changes = this.rowsDiffer.diff(rows);

        if (changes) {
            this.applyChanges(changes, this.rowChangeActions);
        }
    }

    private updateColumnDirectives(columnDirectives: TableColumnDirective[]): void {
        const changes = this.columnDirectivesDiffer.diff(columnDirectives);

        this.columnDirectives = columnDirectives;

        if (changes) {
            this.applyChanges(changes, this.columnDirectiveChangeActions);
            this.tablePluginsDelegate.finalizeCellsRendering();
        }
    }

    private initAvailableRowsCounter(): void {
        if (this.tableBodyEmptyRowDirective && !this.table.disableEmptyRows) {
            const tableAvailableRowsObserver = this.tableBodyEmptyRowDirective.tableAvailableRowsObserver;
            const rowHeightFromStyles = this.getEmptyRowHeightFromStyles();

            if (rowHeightFromStyles) {
                tableAvailableRowsObserver.setRowHeight(rowHeightFromStyles);
            } else {
                tableAvailableRowsObserver.initInstantly(this.table.emptyRow);
            }
        }
    }

    private initEmptyRows(): void {
        if (this.tableBodyEmptyRowDirective && !this.table.disableEmptyRows) {
            const tableEmptyRows = this.tableBodyEmptyRowDirective.tableEmptyRows;

            tableEmptyRows.emptyRow = this.table.emptyRow;
        }
    }

    private updateEmptyRows(): void {
        if (this.tableBodyEmptyRowDirective && !this.table.disableEmptyRows) {
            const tableEmptyRows = this.tableBodyEmptyRowDirective.tableEmptyRows;

            tableEmptyRows.rows = this.table.rows;
            tableEmptyRows.checkForUpdates();
        }
    }

    private applyChanges<T>(changes: IterableChanges<T>, changeActions: ChangeActions<T>): void {
        changes.forEachOperation(
            (record: IterableChangeRecord<T>, adjustedPreviousIndex: number, adjustedCurrentIndex: number) => {
                const changeRecord: ChangeRecord<T> = {
                    item: record.item,
                    previousIndex: record.previousIndex,
                    adjustedPreviousIndex,
                    currentIndex: record.currentIndex,
                    adjustedCurrentIndex
                };

                if (adjustedPreviousIndex === null) {
                    if (changeActions.insert) {
                        changeActions.insert(changeRecord);
                    }
                } else if (adjustedCurrentIndex === null) {
                    if (changeActions.remove) {
                        changeActions.remove(changeRecord);
                    }
                } else {
                    if (changeActions.move) {
                        changeActions.move(changeRecord);
                    }
                }
            });

        if (changeActions.identityChange) {
            changes.forEachIdentityChange((record: IterableChangeRecord<T>) => {
                const changeRecord: ChangeRecord<T> = {
                    item: record.item,
                    previousIndex: record.previousIndex,
                    adjustedPreviousIndex: null,
                    currentIndex: record.currentIndex,
                    adjustedCurrentIndex: null
                };

                changeActions.identityChange(changeRecord);
            });
        }

        if (changeActions.finalize) {
            changeActions.finalize();
        }
    }

    private iterateColumnDirectives(
        columnDefIndex: number,
        callback: (viewContainerRef: ViewContainerRef, columnDirective: TableColumnDirective, rowIndex: number) => void
    ): void {
        const columnCount = this.headerColumnDirectives.length;
        const allDirectives = this.columnDirectives;
        const allDirectiveCount = allDirectives.length;
        const rowCount = Math.ceil(allDirectiveCount / columnCount);

        for (let iRow = 0; iRow < rowCount; iRow++) {
            const absoluteIndex = iRow * columnCount;
            const viewContainerRef = allDirectives[absoluteIndex].viewContainerRef;
            const columnDirective = allDirectives[absoluteIndex + columnDefIndex];

            callback(viewContainerRef, columnDirective, iRow);
        }
    }

    private getColumnConfigChangeActions(): ChangeActions<TableColumnConfig> {
        return {
            insert: ({item, adjustedCurrentIndex, currentIndex}) => {
                const columnDefIndex = this.columnDefIndexByKey[item.key];

                this.tablePluginsDelegate.columnConfigAdded({
                    columnConfig:              item,
                    columnConfigIndex:         currentIndex,
                    adjustedColumnConfigIndex: adjustedCurrentIndex
                });

                this.iterateColumnDirectives(columnDefIndex, (viewContainerRef, columnDirective, rowIndex) =>
                    this.renderColumnCell(
                        columnDirective,
                        viewContainerRef,
                        adjustedCurrentIndex,
                        currentIndex,
                        columnDefIndex,
                        rowIndex
                    )
                );
            },
            remove: ({item, adjustedPreviousIndex}) => {
                const columnDefIndex = this.columnDefIndexByKey[item.key];

                this.tablePluginsDelegate.columnConfigRemoved(adjustedPreviousIndex);

                this.iterateColumnDirectives(columnDefIndex, viewContainerRef =>
                    viewContainerRef.remove(adjustedPreviousIndex)
                );
            },
            move: ({item, adjustedPreviousIndex, adjustedCurrentIndex}) => {
                const columnDefIndex = this.columnDefIndexByKey[item.key];

                this.iterateColumnDirectives(columnDefIndex, viewContainerRef => {
                    const viewRef = viewContainerRef
                        .get(adjustedPreviousIndex) as EmbeddedViewRef<TableColumnTemplateContext>;

                    viewContainerRef.move(viewRef, adjustedCurrentIndex);
                });

                this.tablePluginsDelegate.columnConfigMoved({
                    previousColumnConfigIndex: adjustedPreviousIndex,
                    columnConfigIndex: adjustedCurrentIndex
                });
            },
            identityChange: ({item, currentIndex}) => this.tablePluginsDelegate.columnConfigIdentityChanged({
                columnConfig: item,
                currentIndex
            })
        };
    }

    private getRowChangeActions(): ChangeActions<any> {
        return {
            insert: ({item, adjustedCurrentIndex}) => this.tableBodyRowDirective.createRow(item, adjustedCurrentIndex),
            remove: ({item, adjustedPreviousIndex}) => this.removeBodyRow(adjustedPreviousIndex),
            move: ({adjustedPreviousIndex, adjustedCurrentIndex}) => {
                const rowsViewContainerRef = this.tableBodyRowDirective.viewContainerRef;
                const viewRef = rowsViewContainerRef.get(adjustedPreviousIndex);

                rowsViewContainerRef.move(viewRef, adjustedCurrentIndex);
            },
        // Replacing the row object with the same track key does not update the view at all:
        // $implicit remains a reference to the old object.
        identityChange: ({item, currentIndex}) => {
            const viewRef = this.tableBodyRowDirective.viewContainerRef
                .get(currentIndex) as EmbeddedViewRef<TableBodyRowContext<any>>;

            if (viewRef && viewRef.context) {
                viewRef.context.$implicit = item;
            }
        },
            finalize: () => {
            const rowsViewContainerRef = this.tableBodyRowDirective.viewContainerRef;

            // index is fixed at creation and becomes stale after move/remove/insert.
            // The same VCR also hosts empty-rows and probe — they don't have 'index' in their context.
            for (let iView = 0; iView < rowsViewContainerRef.length; iView++) {
                const viewRef = rowsViewContainerRef.get(iView) as EmbeddedViewRef<any>;

                if (viewRef.context && 'index' in viewRef.context) {
                    viewRef.context.index = iView;
                }
            }

            const rowsVcRefChangeDetectorRef = rowsViewContainerRef.injector
                    .get(ChangeDetectorRef);

                /**
                 * Without calling change detector here there may occur the issue,
                 * where rows are rendered, but empty, since {@link columnDirectivesQueryList}
                 * doesn't detect appearing of new column directives in these new rows.
                 * But anyway, it's required to render rows only after {@link ngAfterContentInit},
                 * so make sure it's not rendered in {@link ngDoCheck}
                 * before {@link ngAfterContentInit} is called.
                 */
                rowsVcRefChangeDetectorRef.detectChanges();
            }
        };
    }

    private getColumnDirectiveChangeActions(): ChangeActions<TableColumnDirective> {
        return {
            insert: ({item, currentIndex}) => {
                const columnCount       = this.headerColumnDirectives.length;
                const columnDefIndex    = currentIndex % columnCount;
                const rowIndex          = (currentIndex - columnDefIndex) / columnCount;
                const columnDef         = this.columnDefs[columnDefIndex];
                const visibleColumnMeta = this.visibleColumnMetaByKey[columnDef.key];

                if (visibleColumnMeta) {
                    const rowFirstColumnIndex = currentIndex - columnDefIndex;

                    this.renderColumnCell(
                        item,
                        this.columnDirectives[rowFirstColumnIndex].viewContainerRef,
                        visibleColumnMeta.viewRefInsertionIndex,
                        visibleColumnMeta.index,
                        columnDefIndex,
                        rowIndex
                    );
                }
            },
            remove: ({item, adjustedPreviousIndex, previousIndex}) => {
                const columnCount      = this.headerColumnDirectives.length;
                const columnDefIndex   = previousIndex % columnCount;
                const adjustedRowIndex = (adjustedPreviousIndex - adjustedPreviousIndex % columnCount) / columnCount;
                const columnDef        = this.columnDefs[columnDefIndex];

                const visibleColumnMeta: VisibleColumnMeta = this.visibleColumnMetaByKey[columnDef.key];

                if (visibleColumnMeta) {
                    this.tablePluginsDelegate.columnCellRemoved({
                        columnConfigIndex: visibleColumnMeta.index,
                        adjustedRowIndex
                    });
                }
            },
            move:   ({item, adjustedPreviousIndex, adjustedCurrentIndex}) => {
                const columnCount    = this.headerColumnDirectives.length;
                const columnDefIndex = adjustedCurrentIndex % columnCount;
                const columnDef      = this.columnDefs[columnDefIndex];

                const visibleColumnMeta: VisibleColumnMeta = this.visibleColumnMetaByKey[columnDef.key];

                if (visibleColumnMeta) {
                    const columnConfigIndex = visibleColumnMeta.index;
                    const adjustedPreviousRowIndex = (adjustedPreviousIndex - adjustedPreviousIndex % columnCount)
                        / columnCount;
                    const adjustedCurrentRowIndex = (adjustedCurrentIndex - adjustedCurrentIndex % columnCount)
                        / columnCount;

                    this.tablePluginsDelegate.onColumnCellMoved({
                        columnConfigIndex,
                        adjustedPreviousRowIndex,
                        adjustedCurrentRowIndex
                    });
                }
            }
        };
    }

    private initTablePlugins(): void {
        this.tablePluginsDelegate.initTablePlugin({
            afterTableViewContainerRef: this.afterTableViewContainerRef,
            tableBodyRulerDirective:    this.tableBodyRowDirective.containerRulerDirective,
            pointerEventsElementRef:    this.elementRef,
            columnsUpdate$:             this.table.columnsUpdate$,
            autoResizeColumns$:         this.table.autoResizeColumns$,
            destroy$:                   this.destroy$,
            message$:                   this.getTablePluginMessagesSubject()
        });
        this.tablePluginsDelegate.columnsSetup({
            columnDefs: this.columnDefs,
            columnsConfig: this.implementedColumnsConfig,
            columnDefIndexByKey: this.columnDefIndexByKey,
            columnsSizingSteps: this.table.columnsSizingSteps
        });
    }

    private renderColumnCell(
        columnDirective: TableColumnDirective,
        viewContainerRef: ViewContainerRef,
        viewRefIndex: number,
        columnConfigIndex: number,
        columnDefIndex: number,
        rowIndex: number
    ): ViewRef {
        const viewRef = columnDirective.render(viewContainerRef, viewRefIndex);

        viewRef.detectChanges();

        this.tablePluginsDelegate.columnCellRendered({
            viewRef,
            columnDirective,
            viewContainerRef,
            viewRefIndex,
            columnConfigIndex,
            columnDefIndex,
            rowIndex
        });

        return viewRef;
    }

    private removeBodyRow(
        adjustedRowIndex: number
    ): void {
        this.tableBodyRowDirective.viewContainerRef.remove(adjustedRowIndex);
        this.tablePluginsDelegate.rowRemoved(adjustedRowIndex + 1); // The first row is header.
    }

    private getColumnsConfig(): TableColumnConfig[] {
        return this.columnsConfigPreview || this.table.columnsConfig;
    }

    private getTablePluginMessagesSubject(): Subject<TablePluginMessage> {
        const messagesSubject$ = new Subject<TablePluginMessage>();

        messagesSubject$.pipe(
            tap(msg => {
                if (msg.type === CommonTablePluginMessageType.SET_COLUMNS_CONFIG) {
                    this.table.columnsConfig = (msg as TablePluginMsgColumnsConfig).columnsConfig;

                    if (!this.columnsConfigPreview) {
                        this.updateColumnsConfig();
                    }

                    if (this.table.columnsUpdate$) {
                        this.table.columnsUpdate$.next(this.table.columnsConfig);
                    }
                }

                if (msg.type === CommonTablePluginMessageType.SET_COLUMNS_CONFIG_PREVIEW) {
                    const newColumnsConfigPreview = (msg as TablePluginMsgColumnsConfigPreview).columnsConfig;
                    const isNothingChanged = this.columnsConfigPreview === null && newColumnsConfigPreview === null;

                    if (!isNothingChanged) {
                        this.columnsConfigPreview = newColumnsConfigPreview;
                        this.updateColumnsConfig();
                    }
                }
            }),
            takeUntil(this.destroy$)
        ).subscribe();

        return messagesSubject$;
    }

    private getEmptyRowHeightFromStyles(): number {
        return parseFloat(
            getComputedStyle(this.elementRef.nativeElement).getPropertyValue(CSS_VAR_TABLE_EMPTY_ROW_HEIGHT)
        ) || undefined;
    }

}
